/**
 * QBO 고객 검색 모달 컴포넌트
 * - Store 등록/수정 폼에서 "QuickBooks에서 가져오기" 버튼으로 열림
 * - 고객 검색 → 선택 시 주소/연락처 자동 입력
 */
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, List, ListItem, ListItemText, ListItemButton,
  Typography, Box, CircularProgress, Chip, InputAdornment, Divider, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { searchQboCustomers, qboFlagOff } from '../Redux/Actions/qboAction';

export default function QboCustomerSearch({ open, onClose, onSelect }) {
  const dispatch = useDispatch();
  const { loading, searchResults, connected, error } = useSelector((s) => s.Qbo);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  // 디바운스 검색
  useEffect(() => {
    if (!query || query.length < 2) return;
    const timer = setTimeout(() => {
      dispatch(searchQboCustomers(query));
    }, 400);
    return () => clearTimeout(timer);
  }, [query, dispatch]);

  const handleClose = () => {
    setQuery('');
    setSelected(null);
    dispatch(qboFlagOff());
    onClose();
  };

  const handleSelect = (customer) => {
    setSelected(customer);
  };

  const handleConfirm = () => {
    if (selected && onSelect) {
      // 선택된 고객 데이터를 Store 폼에 전달
      onSelect({
        qboCustomerId: selected.qboCustomerId,
        name: selected.name,
        address1: selected.address1,
        address2: selected.address2,
        town: selected.town,
        state: selected.state,
        zipcode: selected.zipcode,
        storePhone: selected.storePhone,
        ownerName: selected.ownerName,
        ownerEmail: selected.ownerEmail,
        ownerPhone: selected.ownerPhone,
      });
    }
    handleClose();
  };

  if (!connected) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>QuickBooks 고객 검색</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            QuickBooks가 연결되지 않았습니다. Settings에서 먼저 연결해주세요.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>닫기</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <CloudDownloadIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#2CA01C' }} />
        QuickBooks 고객 검색
      </DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          placeholder="고객명 또는 회사명으로 검색..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, minHeight: 300 }}>
          {/* 좌측: 검색 결과 목록 */}
          <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 400, borderRight: '1px solid #eee' }}>
            {searchResults.length === 0 && query.length >= 2 && !loading && (
              <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                검색 결과가 없습니다.
              </Typography>
            )}
            {query.length < 2 && (
              <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                2자 이상 입력하면 검색됩니다.
              </Typography>
            )}
            <List dense>
              {searchResults.map((c, i) => (
                <ListItem key={i} disablePadding>
                  <ListItemButton
                    selected={selected?.qboCustomerId === c.qboCustomerId}
                    onClick={() => handleSelect(c)}
                  >
                    <ListItemText
                      primary={c.name}
                      secondary={[c.town, c.state].filter(Boolean).join(', ')}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* 우측: 선택된 고객 상세 정보 */}
          <Box sx={{ flex: 1, p: 1 }}>
            {selected ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {selected.name}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  <DetailRow
                    icon={<LocationOnIcon fontSize="small" color="action" />}
                    label="주소"
                    value={[selected.address1, selected.address2, selected.town, selected.state, selected.zipcode].filter(Boolean).join(', ')}
                  />
                  <DetailRow
                    icon={<PersonIcon fontSize="small" color="action" />}
                    label="대표자"
                    value={selected.ownerName}
                  />
                  <DetailRow
                    icon={<PhoneIcon fontSize="small" color="action" />}
                    label="전화"
                    value={selected.storePhone}
                  />
                  <DetailRow
                    icon={<EmailIcon fontSize="small" color="action" />}
                    label="이메일"
                    value={selected.ownerEmail}
                  />
                </Box>

                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #bbf7d0' }}>
                  <Typography variant="caption" color="success.dark">
                    "확인" 버튼을 누르면 위 정보가 Store 폼에 자동 입력됩니다.
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                좌측에서 고객을 선택하면 상세 정보가 표시됩니다.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">취소</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selected}
          sx={{ bgcolor: '#2CA01C', '&:hover': { bgcolor: '#238c16' } }}
        >
          확인 — 폼에 입력
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value || '-'}</Typography>
      </Box>
    </Box>
  );
}
